import { Progress, Spin, Tooltip } from 'antd';
import { ArrowDownload16Regular, CheckmarkCircle16Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import type { ActionTransferProgress, ActionTransferStage } from '../../types/actionTypes';
import { ACTION_STATUS_COLORS } from '../../utils/actionStatusConfig';
import { DMS_TOOLTIP_Z_INDEX } from '../../config/antdTheme';
import './actionModals.css';

interface ActionTransferProgressViewProps {
  compact?: boolean;
  transfer?: ActionTransferProgress | null;
  /** Present only when a downloadable artifact exists for this transfer's target. */
  onDownload?: () => void;
  downloadLabel?: string;
  downloading?: boolean;
  /** Name of the file produced by this transfer, shown once known. */
  fileName?: string;
}

// ACT-UI-F: the doc's canonical stage set, in order. WAITING_FOR_RTU_CONFIRMATION
// reads as "Waiting for RTU" everywhere this view appears.
const STAGE_LABEL_KEYS: Record<Exclude<ActionTransferStage, 'TERMINAL'>, string> = {
  PREPARING: 'Preparing',
  WAITING_FOR_RTU_CONFIRMATION: 'Waiting for RTU',
  TRANSFERRING: 'Transferring',
  VERIFYING: 'Verifying',
  READY: 'Ready',
};

const formatBytes = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const amount = value / (1024 ** index);
  return `${amount >= 10 || index === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[index]}`;
};

const ActionTransferProgressView = ({
  compact = false,
  transfer,
  onDownload,
  downloadLabel,
  downloading = false,
  fileName,
}: ActionTransferProgressViewProps) => {
  const { t } = useTranslation();
  if (!transfer || transfer.stage === 'TERMINAL') return null;
  const stage = transfer.stage as Exclude<ActionTransferStage, 'TERMINAL'>;
  const total = transfer.bytesTotal && transfer.bytesTotal > 0 ? transfer.bytesTotal : null;
  const percent = total
    ? Math.min(100, Math.round((Math.max(0, transfer.bytesTransferred) / total) * 100))
    : null;
  const stageLabel = t(`actionsModal.transfer.stage.${stage}`, { defaultValue: STAGE_LABEL_KEYS[stage] });
  const bytesLabel = total
    ? `${formatBytes(transfer.bytesTransferred)} / ${formatBytes(total)}`
    : formatBytes(transfer.bytesTransferred);
  const isReady = stage === 'READY';
  const resolvedDownloadLabel = downloadLabel || t('actionsModal.artifacts.downloadReady', { defaultValue: 'Download file' });

  return <div className={`action-transfer-progress${compact ? ' action-transfer-progress--compact' : ''}`} data-testid="action-transfer-progress">
    <div className="action-transfer-progress__label">
      <span className="action-transfer-progress__status">
        {isReady
          ? <CheckmarkCircle16Filled aria-hidden="true" style={{ color: ACTION_STATUS_COLORS.COMPLETED }} />
          : <Spin size="small" />}
        <span className="action-transfer-progress__stage">{stageLabel}</span>
        {fileName ? (
          <span className="action-transfer-progress__filename" title={fileName}>{fileName}</span>
        ) : null}
      </span>
      <span className="action-transfer-progress__trailing">
        {(stage === 'TRANSFERRING' || stage === 'VERIFYING' || isReady) ? <small>{bytesLabel}</small> : null}
        {isReady && onDownload ? (
          <Tooltip title={resolvedDownloadLabel} zIndex={DMS_TOOLTIP_Z_INDEX}>
            <button
              type="button"
              className="action-transfer-progress__download"
              onClick={onDownload}
              disabled={downloading}
              aria-label={resolvedDownloadLabel}
            >
              {downloading ? <Spin size="small" /> : <ArrowDownload16Regular aria-hidden="true" />}
            </button>
          </Tooltip>
        ) : null}
      </span>
    </div>
    {percent !== null ? <Progress percent={percent} size="small" showInfo={!compact} status={isReady ? 'success' : 'active'} /> : null}
  </div>;
};

export default ActionTransferProgressView;
